// Mag deze sleepactie doorgaan zonder een toon-als-regel te breken?
//
// De hele conditionele logica leunt op één invariant: een triggervraag staat
// altijd VÓÓR de vraag die ervan afhangt. Dat is wat kringverwijzingen
// onmogelijk maakt en wat garandeert dat de respondent de trigger al gezien
// heeft. De bouwer bewaakt dat bij het instellen (alleen eerdere vragen zijn
// kiesbaar) — maar slepen kan het achteraf alsnog omdraaien.
//
// BLOKKEREN, NIET STIL WISSEN. De verleiding is om de regel dan maar weg te
// gooien; dat is precies het gedrag waarbij verborgen UI zonder signaal
// terugkomt. De gebruiker krijgt een melding en de volgorde blijft zoals hij
// was.
// AI-CONTEXT: See docs/pitfalls.md#ui-removed-prop-resurrects-default
//
// De projectie hieronder bootst na waar het veld ná de drop terechtkomt, en
// controleert de invariant op die uitkomst. Dat is bewust losgekoppeld van hoe
// performDrop de posities wegschrijft: één pure functie, testbaar, zonder
// Firestore.
//
// AI-CONTEXT: See docs/features/conditional-questions.md

import flattenFieldsInOrder from './flattenFieldsInOrder'
import isStandardQuestion from './isStandardQuestion'
import isQualifyingQuestion from './isQualifyingQuestion'

// Kwalificeringsvragen staan vast bovenaan in hun eigen sectie: niet te
// verslepen, er komt niets in en niets boven. Alleen direct ná de sectie mag.
// AI-CONTEXT: See docs/features/sector-kwalificeringsvragen.md
const QUALIFYING_LOCKED = 'De kwalificeringsvragen staan vast bovenaan. Je zet ze aan en uit bij het onderzoek.'

/**
 * @typedef {object} ReorderVerdict
 * @property {boolean} allowed
 * @property {string} reason - Uitleg voor de gebruiker; leeg als het mag
 */

/**
 * @param {object} args
 * @param {Array<object>} args.fields - Alle velden van de vragenlijst
 * @param {object} args.dragged - Het veld dat versleept wordt
 * @param {object} args.target - Het veld waarop losgelaten wordt
 * @param {'before'|'after'|'inside'} args.edge - Waar ten opzichte van het doel
 * @returns {ReorderVerdict}
 */
const canReorderWithRules = ({ fields, dragged, target, edge } = {}) => {
    const allow = { allowed: true, reason: '' }
    if (!Array.isArray(fields) || !dragged?.ID || !target?.ID) return allow
    if (dragged.ID === target.ID) return allow

    const afterQualifyingSection = target.Type === 'section' && edge === 'after'
    if (isQualifyingQuestion(dragged) || (isQualifyingQuestion(target) && !afterQualifyingSection)) {
        return { allowed: false, reason: QUALIFYING_LOCKED }
    }

    const ordered = flattenFieldsInOrder(fields)

    // Een sectie reist met haar eigen vragen mee.
    const moving = dragged.Type === 'section'
        ? ordered.filter((item) => item?.ID === dragged.ID || item?.SectionID === dragged.ID)
        : [dragged]
    const movingIds = new Set(moving.map((item) => item.ID))

    const rest = ordered.filter((item) => !movingIds.has(item?.ID))
    const targetIndex = rest.findIndex((item) => item?.ID === target.ID)
    if (targetIndex < 0) return allow

    // Hoeveel vragen hangen er in het doel, ná het weghalen van het gesleepte blok?
    const targetChildren = target.Type === 'section'
        ? rest.filter((item) => item?.SectionID === target.ID).length
        : 0

    let insertAt
    if (edge === 'inside' && target.Type === 'section') {
        // Loslaten op de sectiekop zelf zet het veld achteraan in die sectie.
        insertAt = targetIndex + 1 + targetChildren
    } else if (edge === 'after') {
        // Achter een sectie betekent: achter de hele sectie, kop én vragen.
        insertAt = targetIndex + 1 + targetChildren
    } else {
        insertAt = targetIndex
    }

    const projected = [...rest.slice(0, insertAt), ...moving, ...rest.slice(insertAt)]
    const indexById = new Map(projected.map((item, index) => [item?.ID, index]))

    for (const item of projected) {
        if (isStandardQuestion(item)) continue

        const triggerId = item?.ShowWhen?.FieldID
        if (!triggerId) continue

        const triggerIndex = indexById.get(triggerId)
        // Trigger bestaat niet meer → de regel doet toch al niets (fail-open).
        if (triggerIndex === undefined) continue

        if (triggerIndex >= indexById.get(item.ID)) {
            const trigger = projected[triggerIndex]
            return {
                allowed: false,
                reason: `'${item.Question}' hangt af van '${trigger?.Question}'. Die vraag moet ervóór blijven staan.`,
            }
        }
    }

    return allow
}

export default canReorderWithRules
